import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * ActionDrawer Component - Best Practice Drawer Implementation
 * @param {Object} props
 * @param {boolean} props.isOpen - Drawer açık/kapalı durumu
 * @param {function} props.onClose - Drawer kapatma fonksiyonu
 * @param {React.ReactNode} props.title - Drawer başlığı
 * @param {string} props.subtitle - Drawer alt başlığı (opsiyonel)
 * @param {React.ReactNode} props.children - Drawer içeriği
 * @param {React.ReactNode} props.footer - Drawer footer içeriği (opsiyonel)
 * @param {string} props.width - Drawer genişliği (default: '500px')
 * @param {boolean} props.closeOnBackdrop - Backdrop'a tıklayınca kapansın mı? (default: true)
 * @param {boolean} props.closeOnEscape - ESC tuşuna basınca kapansın mı? (default: true)
 * @param {string} props.position - Drawer pozisyonu ('right' veya 'left', default: 'right')
 * @param {boolean} props.fullScreenOnMobile - Mobilde tam ekran olsun mu? (default: true)
 * @param {string} props.ariaLabel - ARIA label for accessibility
 */
const ActionDrawer = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = '500px',
  closeOnBackdrop = true,
  closeOnEscape = true,
  position = 'right',
  fullScreenOnMobile = true,
  ariaLabel
}) => {
  const overlayRef = useRef(null);
  const drawerRef = useRef(null);
  const previousActiveElement = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Responsive detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard navigation (ESC key)
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeOnEscape, onClose]);

  // Focus management and body scroll lock
  useEffect(() => {
    if (isOpen) {
      // Store previous active element
      previousActiveElement.current = document.activeElement;
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      // Focus first focusable element after animation
      setTimeout(() => {
        const firstFocusable = drawerRef.current?.querySelector(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }, 150);
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      
      // Restore focus to previous element
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  // Focus trap - keep focus within drawer
  useEffect(() => {
    if (!isOpen) return;

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = drawerRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      
      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  const drawerWidth = isMobile && fullScreenOnMobile ? '100vw' : width;
  const drawerPosition = position === 'left' ? { left: 0 } : { right: 0 };
  const transformValue = position === 'left' 
    ? (isOpen ? 'translateX(0)' : 'translateX(-100%)')
    : (isOpen ? 'translateX(0)' : 'translateX(100%)');

  const drawerContent = (
    <>
      <div
        ref={overlayRef}
        className="drawer-overlay"
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040,
          transition: 'opacity 0.3s ease-out',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none'
        }}
        aria-hidden="true"
      />

      <div
        ref={drawerRef}
        className="drawer-panel bg-white shadow-lg d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "drawer-title" : undefined}
        aria-label={ariaLabel || (typeof title === 'string' ? title : undefined)}
        style={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          ...drawerPosition,
          width: drawerWidth,
          maxWidth: '100vw',
          zIndex: 1050,
          transform: transformValue,
          transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="drawer-header p-4 border-bottom d-flex align-items-center justify-content-between bg-white" style={{ flexShrink: 0 }}>
          <div className="flex-grow-1">
            {typeof title === 'string' ? (
              <h5 id="drawer-title" className="mb-1 text-dark fw-bold" style={{ fontSize: '1.25rem' }}>
                {title}
              </h5>
            ) : (
              <div id="drawer-title">{title}</div>
            )}
            {subtitle && (
              <p className="text-muted small mb-0 mt-1" style={{ fontSize: '0.875rem' }}>
                {subtitle}
              </p>
            )}
          </div>
          <button 
            className="btn btn-sm btn-light rounded-circle ms-3" 
            onClick={onClose}
            aria-label="Kapat"
            style={{
              width: '32px',
              height: '32px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Body */}
        <div 
          className="drawer-body p-4 overflow-auto flex-grow-1"
          style={{
            minHeight: 0,
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="drawer-footer p-3 border-top bg-white" style={{ flexShrink: 0 }}>
            {footer}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }

        .drawer-body::-webkit-scrollbar {
          width: 8px;
        }

        .drawer-body::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .drawer-body::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        .drawer-body::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        @media (max-width: 767px) {
          .drawer-panel {
            width: 100vw !important;
          }
        }
      `}</style>
    </>
  );

  return ReactDOM.createPortal(drawerContent, document.body);
};

export default ActionDrawer;
